// build-carousel.ts — the ONE front-end carousel renderer (render_carousel() twin, functions.php L15728).
//
// Promoted out of object-card/card-shell.ts (2026-06-13, plans/render-carousel-retirement.md Phase 0) so
// a SINGLE renderer serves both consumers with no drift:
//   • the cache-rendered caster cards (render-creature-card.ts / render-player-card.ts call buildCarousel
//     directly, blob-first), and
//   • the global hydrator (carousel/index.ts → window.ftMountCarousels), which parses the
//     <script type="application/json"> island emitted by ft_carousel_mount() and calls buildCarousel.
//
// This module is deliberately self-contained (its own esc/iconPh copies + scalarLayerSlot from
// array-types) to avoid a circular import with card-shell, which re-exports buildCarousel from here.
//
// Markup, indicator/control gating (>1), active-index, video-over-picture priority, and the
// permission-gated encounter-creature / inventory set-pic buttons mirror the PHP exactly so the server
// fallback and the client render are visually identical.

import { scalarLayerSlot } from './array-types';

// ── tiny local twins (verbatim from card-shell.ts so output stays byte-identical) ─────────────────
function esc(v: unknown): string {
    return String(v ?? '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

/** icon_ph() twin — inert placeholder ftMountIcons upgrades via iconMap. */
function iconPh(key: unknown, w = 25, h = 25, title?: string): string {
    const k = typeof key === 'object' && key !== null ? String(scalarLayerSlot(key) ?? '') : String(key ?? '');
    if (!k) return '';
    const t = title ? ` data-title='${esc(title)}'` : '';
    return `<i class='ft-icon' data-icon='${esc(k)}' data-w='${w}' data-h='${h}'${t} style='display:inline-block;width:${w}px;height:${h}px' aria-hidden='true'></i>`;
}

// ── carousel media readers (bare-or-[0]-wrapped tolerant, like the PHP) ───────────────────────────
const _carUnwrap = (v: any): any => (Array.isArray(v) ? v[0] : v);
function _carPicId(p: any): string { return String(_carUnwrap(p?.picture_id ?? p?.pic_id) ?? ''); }
function _carPicUrl(p: any): string { return String(_carUnwrap(p?.picture_url ?? p?.pic_url ?? p?.url) ?? ''); }
function _carVidUrl(v: any): string { return String(_carUnwrap(v?.video_url ?? v?.vid_url ?? v?.url) ?? ''); }
function _carVidMime(v: any): string {
    const mime = String(_carUnwrap(v?.video_mime_type) ?? '');
    if (mime !== '') return mime;
    const ext = String(_carUnwrap(v?.video_file_extension) ?? '');
    return ext !== '' ? `video/${ext}` : '';
}

/** Set-presentation / set-thumbnail buttons (permission-gated by the caller — pass null to omit). */
export interface CarouselSetPic {
    /** 'creature' → update_encounter_creature_pic_id; 'inventory' → update_inventory_pic_id. */
    kind?: 'creature' | 'inventory';
    /** encounter_creature_id (creature) or inventory_id (inventory) — the update target id. */
    encounterCreatureId: string;
    presentationPicId: string;
    thumbnailPicId: string;
}

export function buildCarousel(opts: {
    /** Unique id token — the container becomes `carouselIndicators_<domId>` (sanitized). */
    domId: string;
    itemName: string;
    pics: any[];
    videos?: any[];
    /** Max image/video height in px (PHP passes 355 for the creature card). */
    height?: number;
    /** Picture id that should open active (creature thumbnail/presentation pic); ignored for videos. */
    activePicId?: string | null;
    setPic?: CarouselSetPic | null;
}): string {
    const videos = Array.isArray(opts.videos) ? opts.videos : [];
    const pics = Array.isArray(opts.pics) ? opts.pics : [];
    const isVideo = videos.length > 0;          // PHP prioritizes videos over pictures
    const media = isVideo ? videos : pics;
    if (media.length === 0) return '';          // render_carousel returns '' on no media

    const height = opts.height ?? 355;
    const cid = `carouselIndicators_${String(opts.domId).replace(/[^A-Za-z0-9_]/g, '')}`;
    const name = esc(opts.itemName);

    // Active index: creature thumbnail/presentation pic for pictures; videos always open at 0.
    let activeIndex = 0;
    if (!isVideo && opts.activePicId) {
        const want = String(opts.activePicId);
        const found = pics.findIndex((p) => _carPicId(p) === want);
        if (found >= 0) activeIndex = found;
    }

    // ONE "set as default picture" button (the presentation pic). The thumbnail pic was collapsed into the presentation pic
    // for encounter creatures + inventory (2026-06-19): they were always written in lockstep (both upload
    // handlers + the build readers mirror thumbnail = xz), the combat log never read the thumbnail pointer,
    // and we don't crop a separate log variant — so the second button just let them drift apart. The server
    // endpoints now write BOTH columns to this id, so a single button is the whole story. (PC thumbnails are
    // a separate system — player_character_picture flags — and are untouched.)
    const setPicButtons = (p: any): string => {
        if (!opts.setPic || isVideo) return '';
        const cur = _carPicId(p);
        if (cur === '') return '';
        const sp = opts.setPic;
        const presentationPicId = sp.presentationPicId || '';
        if (cur === presentationPicId) return '';
        const isInv = sp.kind === 'inventory';
        const fn = isInv ? 'update_inventory_pic_id' : 'update_encounter_creature_pic_id';
        const presentationField = isInv ? 'inventory_presentation_pic_id' : 'encounter_creature_presentation_pic_id';
        const presentationIcon = isInv ? 'set_inventory_presentation_pic_id' : 'set_encounter_creature_presentation_pic_id';
        const presentationBtn = `<button class="btn btn-sm btn-primary set-presentation-pic" onclick="${fn}('${esc(sp.encounterCreatureId)}', '${presentationField}', '${esc(cur)}', this)">${iconPh(presentationIcon)}</button>`;
        return `<div class="carousel-buttons position-absolute top-0 end-0 p-2 d-flex flex-column gap-2" style="margin-right: 75px; z-index: 10;">${presentationBtn}</div>`;
    };

    const multi = media.length > 1;
    const indicators = multi
        ? media.map((_, i) =>
            `<button type="button" data-bs-target="#${cid}" data-bs-slide-to="${i}" aria-label="${name} Picture ${i + 1}"${i === activeIndex ? ' class="active" aria-current="true"' : ''}></button>`
        ).join('')
        : '';

    const items = media.map((m, i) => {
        const active = i === activeIndex ? 'active' : '';
        if (isVideo) {
            return `<div class="carousel-item ${active}"><video src="${esc(_carVidUrl(m))}" type="${esc(_carVidMime(m))}" class="d-block mx-auto img-fluid" style="object-fit: contain; width: auto; max-height:${height}px;" muted playsinline preload="none"></video></div>`;
        }
        return `<div class="carousel-item ${active}"><img src="${esc(_carPicUrl(m))}" loading="lazy" decoding="async" class="d-block mx-auto img-fluid" style="object-fit: contain; width: auto; max-height:${height}px;" alt="${name} ${i + 1}">${setPicButtons(m)}</div>`;
    }).join('');

    const controls = multi
        ? `<button class="carousel-control-prev" type="button" data-bs-target="#${cid}" data-bs-slide="prev"><span class="carousel-control-prev-icon" aria-hidden="true"></span><span class="visually-hidden">Previous</span></button><button class="carousel-control-next" type="button" data-bs-target="#${cid}" data-bs-slide="next"><span class="carousel-control-next-icon" aria-hidden="true"></span><span class="visually-hidden">Next</span></button>`
        : '';

    // Hidden-input identity: creature → carousel-encounter-creature-id, inventory → carousel-inventory-id.
    const hiddenInputs = opts.setPic
        ? `<input type="hidden" class="${opts.setPic.kind === 'inventory' ? 'carousel-inventory-id' : 'carousel-encounter-creature-id'}" value="${esc(opts.setPic.encounterCreatureId)}"><input type="hidden" class="carousel-presentation-pic-id" value="${esc(opts.setPic.presentationPicId || '')}"><input type="hidden" class="carousel-thumbnail-pic-id" value="${esc(opts.setPic.thumbnailPicId)}">`
        : '';

    const videoClass = isVideo ? ' video-carousel' : '';
    return `<div id="${cid}" class="carousel slide w-100${videoClass} position-relative" data-bs-ride="false"><div class="carousel-indicators">${indicators}</div><div class="carousel-inner text-center">${items}</div>${controls}${hiddenInputs}</div>`;
}
